import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaMobileAlt,
  FaSignInAlt,
  FaBuilding,
  FaArrowLeft,
} from "react-icons/fa";
import styled from "styled-components";
import { motion } from "framer-motion";
import backImage from "./assets/back.jpg";
import logo from "./assets/Logo2.png";
import { RECAPTCHA_SITE_KEY, loadRecaptcha } from "./recaptcha";

const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-image: url(${backImage});
  background-size: cover;
  background-position: center;
`;

const ValidationError = styled.div`
  color: red;
  font-size: 13px;
  font-weight: 500;
  margin-top: -15px;
  margin-bottom: 10px;
  width: 100%;
  text-align: left;
  padding-left: 5px;
  animation: fadein 0.3s ease-in-out;

  @keyframes fadein {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const RegisterForm = styled(motion.form)`
  font-family: "Inter", sans-serif;
  background: rgba(48, 136, 139, 0.15); /* Glass effect */
  backdrop-filter: blur(12px);
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const Logo = styled.img`
  width: 200px;
  margin-bottom: 20px;
`;

const InputField = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 100%;
  input {
    width: 100%;
    padding: 12px 50px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: bold;
    color: #333;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(5px);
    outline: none;
    transition: all 0.3s ease-in-out;
    &:focus {
      background: rgba(255, 255, 255, 0.3);
      box-shadow: 0px 0px 12px rgba(255, 255, 255, 0.4);
    }
    &::placeholder {
      font-weight: normal;
      color: rgba(46, 42, 42, 0.7);
    }
  }
  svg {
    position: absolute;
    top: 50%;
    left: 15px;
    transform: translateY(-50%);
    color: rgba(46, 42, 42, 0.7);
  }
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  padding: 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  transition: all 0.3s ease-in-out;
  &:hover {
    background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
    transform: scale(1.05);
  }
`;

const MessageContainer = styled.div`
  text-align: center;
  margin-top: 20px;
  font-weight: bold;
  color: ${(props) => (props.success ? "green" : "red")};
`;

const AdminLink = styled(Link)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  padding: 10px;
  border-radius: 50%;
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  transition: all 0.3s ease-in-out;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
    color: #4facfe;
  }
`;

const SiteLink = styled.a`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  padding: 8px 14px;
  border-radius: 9999px; /* pill */
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: black;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    color: #4facfe;
  }
`;

const StyledLink = styled(Link)`
  margin-top: 15px;
  color: rgba(46, 42, 42, 0.7);
  font-weight: bold;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px; /* Space between icon and text */
  transition: 0.3s;

  &:hover {
    color: white;
  }
`;

const InfoAlert = styled.div`
  width: 100%;
  margin-top: 12px;
  padding: 12px 14px;
  border-left: 4px solid #f59e0b; /* amber */
  background: rgba(255, 193, 7, 0.12);
  color: #3f3f46;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.45;

  code {
    background: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      "Liberation Mono", monospace;
  }
  em {
    font-style: normal;
    font-weight: 600;
  }
`;

function Register() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [usernameCheck, setUsernameCheck] = useState({
    checking: false,
    available: null,
  });

  const [emailCheck, setEmailCheck] = useState({
    checking: false,
    exists: null,
  });

  const [errors, setErrors] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    confirmEmail: "",
    company: "",
    phone: "",
  });

  // 🔔 fire-and-forget webhook for normal register
  async function sendMakeWebhook(payload) {
    try {
      await axios.post(
        "https://hook.eu2.make.com/sgo2su45jhub89h2eq0kwexzst1iavdb",
        payload,
        { timeout: 8000 }
      );
    } catch (err) {
      console.warn(
        "Make webhook error (register):",
        err?.response?.status || err?.message
      );
    }
  }

  useEffect(() => {
    loadRecaptcha().catch((err) => {
      console.error("reCAPTCHA load error (register):", err);
    });
  }, []);

  // username availability check
  useEffect(() => {
    if (!username) {
      setUsernameCheck({ checking: false, available: null });
      return;
    }
    const id = setTimeout(async () => {
      try {
        setUsernameCheck({ checking: true, available: null });
        const { data } = await axios.post(
          "https://server229256.nazwa.pl/api/check-username",
          {
            username: username.trim(),
          }
        );
        setUsernameCheck({ checking: false, available: data.available });
      } catch {
        setUsernameCheck({ checking: false, available: null });
      }
    }, 400); // debounce 400ms
    return () => clearTimeout(id);
  }, [username]);

  // email existence check (always lowercase)
  useEffect(() => {
    const trimmed = email.trim().toLowerCase();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!trimmed || !isValid) {
      setEmailCheck({ checking: false, exists: null });
      return;
    }
    const id = setTimeout(async () => {
      try {
        setEmailCheck({ checking: true, exists: null });
        const { data } = await axios.post(
          "https://server229256.nazwa.pl/api/check-email",
          { email: trimmed }
        );
        setEmailCheck({ checking: false, exists: !!data.exists });
      } catch {
        setEmailCheck({ checking: false, exists: null });
      }
    }, 400);
    return () => clearTimeout(id);
  }, [email]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccess(false);
    setLoading(true);

    // block trailing spaces for username + force lowercase for emails
    const cleanUsername = username.replace(/\s+$/, "");
    const cleanEmail = email.replace(/\s+$/, "").toLowerCase();
    const cleanConfirmEmail = confirmEmail.replace(/\s+$/, "").toLowerCase();

    if (username !== cleanUsername) {
      setUsername(cleanUsername);
    }
    if (email !== cleanEmail) {
      setEmail(cleanEmail);
    }
    if (confirmEmail !== cleanConfirmEmail) {
      setConfirmEmail(cleanConfirmEmail);
    }

    if (password !== confirmPassword) {
      setMessage("Hasła się nie zgadzają");
      setLoading(false);
      return;
    }

    if (cleanEmail !== cleanConfirmEmail) {
      setMessage("E-maile nie pasują do siebie");
      setLoading(false);
      return;
    }

    try {
      // 🔒 reCAPTCHA v3 – load script and get token
      let recaptchaToken = null;
      try {
        const grecaptcha = await loadRecaptcha();
        recaptchaToken = await grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action: "register",
        });
      } catch (err) {
        console.error("reCAPTCHA load/execute error:", err);
        setMessage("Błąd bezpieczeństwa: reCAPTCHA nie jest dostępna.");
        setLoading(false);
        return;
      }

      // Call backend with token
      await axios.post("https://server229256.nazwa.pl/register", {
        username: cleanUsername,
        email: cleanEmail,
        confirmEmail: cleanConfirmEmail,
        phone,
        company,
        password,
        recaptchaToken,
      });

      // webhook on successful register
      sendMakeWebhook({
        event: "user_register_success",
        username: cleanUsername,
        email: cleanEmail,
        phone,
        company,
        registered_at: new Date().toISOString(),
        page_url:
          typeof window !== "undefined" ? window.location.href : "",
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : "",
        source: "AnalizyIRaporty.pl",
      });

      setMessage(
        "Rejestracja zakończona! Wysłaliśmy e-mail z linkiem aktywacyjnym."
      );
      setSuccess(true);
      // navigate("/login"); // optional
    } catch (error) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;

      if (status === 409) {
        setMessage(
          serverMsg ||
            "Nazwa użytkownika lub e-mail są już zajęte. Wybierz inne dane."
        );
      } else if (status === 400) {
        setMessage(serverMsg || "Błędne dane formularza.");
      } else if (status === 403) {
        setMessage(serverMsg || "Weryfikacja reCAPTCHA nie powiodła się.");
      } else {
        setMessage("Błąd podczas rejestracji");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <SiteLink
        href="https://www.analizyiraporty.pl/"
        onClick={(e) => {
          e.preventDefault();
          window.location.href = "https://www.analizyiraporty.pl/";
        }}
        aria-label="Powrót do strony głównej"
      >
        <FaArrowLeft size={14} />
        Powrót do strony
      </SiteLink>

      <AdminLink to="/adminpanel/adminlogin">
        <FaLock size={20} />
      </AdminLink>

      <RegisterForm
        as="form"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onSubmit={handleRegister}
      >
        <Logo src={logo} alt="Logo" />

        {/* Username */}
        <InputField>
          <FaUser />
          <input
            type="text"
            placeholder="Nazwa użytkownika *"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </InputField>

        {usernameCheck.available === false && (
          <ValidationError>
            Nazwa użytkownika jest już zajęta. Wybierz inną.
          </ValidationError>
        )}
        {usernameCheck.checking && (
          <ValidationError style={{ color: "blue" }}>
            Sprawdzanie dostępności...
          </ValidationError>
        )}

        {/* Password */}
        <InputField>
          <FaLock />
          <input
            type="password"
            placeholder="Hasło *"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({
                ...prev,
                password:
                  e.target.value.length < 6
                    ? "Hasło musi mieć min. 6 znaków"
                    : "",
                confirmPassword:
                  confirmPassword && e.target.value !== confirmPassword
                    ? "Hasła się nie zgadzają"
                    : "",
              }));
            }}
            required
          />
        </InputField>
        {errors.password && (
          <ValidationError>{errors.password}</ValidationError>
        )}

        {/* Confirm Password */}
        <InputField>
          <FaLock />
          <input
            type="password"
            placeholder="Potwierdź hasło *"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors((prev) => ({
                ...prev,
                confirmPassword:
                  e.target.value !== password
                    ? "Hasła się nie zgadzają"
                    : "",
              }));
            }}
            required
          />
        </InputField>
        {errors.confirmPassword && (
          <ValidationError>{errors.confirmPassword}</ValidationError>
        )}

        {/* Email */}
        <InputField>
          <FaEnvelope />
          <input
            type="email"
            placeholder="Email *"
            value={email}
            onChange={(e) => {
              const raw = e.target.value.replace(/\s+$/, "");
              const val = raw.toLowerCase();
              setEmail(val);
              setErrors((prev) => ({
                ...prev,
                email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
                  ? ""
                  : "Niepoprawny adres email",
                confirmEmail:
                  confirmEmail && val !== confirmEmail
                    ? "E-maile się nie zgadzają"
                    : "",
              }));
            }}
            style={{ textTransform: "lowercase" }}
            required
          />
        </InputField>
        {errors.email && <ValidationError>{errors.email}</ValidationError>}

        {emailCheck?.exists === true && (
          <ValidationError>
            Ten adres e-mail jest już używany. Wybierz inny.
          </ValidationError>
        )}
        {emailCheck?.checking && (
          <ValidationError style={{ color: "blue" }}>
            Sprawdzanie dostępności e-maila...
          </ValidationError>
        )}

        {/* Confirm Email */}
        <InputField>
          <FaEnvelope />
          <input
            type="email"
            placeholder="Potwierdź Email *"
            value={confirmEmail}
            onChange={(e) => {
              const raw = e.target.value.replace(/\s+$/, "");
              const val = raw.toLowerCase();
              setConfirmEmail(val);
              setErrors((prev) => ({
                ...prev,
                confirmEmail: val !== email ? "E-maile się nie zgadzają" : "",
              }));
            }}
            style={{ textTransform: "lowercase" }}
            required
          />
        </InputField>
        {errors.confirmEmail && (
          <ValidationError>{errors.confirmEmail}</ValidationError>
        )}

        {/* Company */}
        <InputField>
          <FaBuilding />
          <input
            type="text"
            placeholder="Nazwa firmy *"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            required
          />
        </InputField>

        {/* Phone */}
        <InputField>
          <FaMobileAlt />
          <input
            type="tel"
            placeholder="Numer telefonu *"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setErrors((prev) => ({
                ...prev,
                phone: /^[0-9]{9,15}$/.test(e.target.value)
                  ? ""
                  : "Numer musi mieć 9-15 cyfr",
              }));
            }}
            pattern="[0-9]{9,15}"
            title="Podaj poprawny numer telefonu"
            required
          />
        </InputField>
        {errors.phone && <ValidationError>{errors.phone}</ValidationError>}

        {/* Submit button */}
        <Button
          type="submit"
          whileTap={{ scale: 0.95 }}
          disabled={
            loading ||
            usernameCheck.available === false ||
            emailCheck.exists === true
          }
        >
          {loading ? "Rejestracja..." : "Zarejestruj się"}
        </Button>

        {loading && (
          <p style={{ marginTop: "15px", color: "blue" }}>
            Trwa rejestracja...
          </p>
        )}

        <MessageContainer success={success ? "true" : "false"}>
          {message}
        </MessageContainer>

        {success && (
          <InfoAlert>
            <strong>Nie widzisz wiadomości?</strong>
            <br />
            Sprawdź foldery <em>Spam</em> / <em>Oferty</em> / <em>Inne</em>.
            Dodaj nadawcę <code>biuro@analizyiraporty.pl</code> do kontaktów,
            aby kolejne maile trafiały do skrzynki odbiorczej. <br />
            Temat wiadomości:{" "}
            <em>„Potwierdź swoje konto – AnalizyIRaporty.pl”</em>.
          </InfoAlert>
        )}

        <StyledLink to="/login">
          <FaSignInAlt /> Masz już konto? Zaloguj się tutaj
        </StyledLink>
      </RegisterForm>
    </RegisterContainer>
  );
}

export default Register;
